import { connect, ConnectedProps } from 'react-redux';

import { RootState } from '../../../../lib/store';
import { ChartDataRange } from '../../../../lib/store/market-data/charting/types';
import { isSymbolETF as isETF, isUserVerifiedStatus } from '../../../../lib/store/selectors';
import BigChartCache from '../../../../lib/store-util/BigChartCache';

import { Preloader } from '../../../../components';
import ScopeButtons from '../../../../components/Chart/ScopeButtons/ScopeButtons';
import { ChartContainer } from '../../../../components/Chart/SymbolChart/ChartContainer';
import TradeViewChartButton from '../../../../components/Chart/TradingViewChartButton/TradeViewChartButton';
import CompanyDetails from '../../../../components/SymbolProfile/CompanyDetails/CompanyDetails';
import KeyStatistics from '../../../../components/SymbolProfile/KeyStatistics/KeyStatistics';
import PendingOrders from '../../../../components/SymbolProfile/PendingOrders/PendingOrders';
import PeopleAlsoTrade from '../../../../components/SymbolProfile/PeopleAlsoTrade/PeopleAlsoTrade';
import SharesOwned from '../../../../components/SymbolProfile/SharesOwned/SharesOwned';
import { useIsMobile } from '../../../../hooks';
import { CHART_HEIGHT_MOBILE, CHART_HEIGHT_WEB } from '../../../../shared_components/CanvasChart/constants';

import styles from './Overview.module.scss';

type Props = {
  symbol: string;
  currentRange: ChartDataRange;
  setCurrentRange: (range: ChartDataRange) => void;
} & ReduxProps;

const Overview = (props: Props) => {
  const {
    symbol,
    individualData,
    isSymbolETF,
    companyLogoAndName,
    currentRange,
    setCurrentRange,
  } = props;

  const { isMobile } = useIsMobile();
  const isUserVerified = isUserVerifiedStatus(individualData);

  const { dataByRange: chartDataByRange } = BigChartCache.use();
  const dataByRange = currentRange && chartDataByRange ? chartDataByRange[currentRange] : undefined;

  return (
    <div>
      {isMobile && (
        <div className={styles.scopeButtonsWrapperMobile}>
          <ScopeButtons
            symbol={symbol}
            chartDataByRange={chartDataByRange}
            changeRange={setCurrentRange}
            onScopeButtonClick={() => { }}
          />
          <TradeViewChartButton symbol={symbol!} />
        </div>
      )}
      <div id="charContainer" className={styles.chartContainer}>
        {!dataByRange ? (
          <div className={styles.chartPreloader} style={{ height: isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_WEB }}>
            <Preloader owner="ChartContainer" />
          </div>
        ) : (
          <div className={styles.chartWrapper}>
            <ChartContainer
              symbol={symbol}
              dataSet={dataByRange}
              range={currentRange}
              companyName={companyLogoAndName[symbol]?.name}
            />
          </div>
        )}
        {!isMobile && (
          <div className={styles.scopeButtonsWrapper}>
            <ScopeButtons
              symbol={symbol}
              chartDataByRange={chartDataByRange}
              changeRange={setCurrentRange}
              onScopeButtonClick={() => { }}
            />
            <TradeViewChartButton symbol={symbol!} />
          </div>
        )}
      </div>
      <div className={isMobile ? styles.sectionMobile : styles.section}>
        <SharesOwned symbol={symbol} />
      </div>
      <div className={isMobile ? styles.sectionMobile : styles.section}>
        <PendingOrders symbol={symbol} />
      </div>
      <div className={isMobile ? styles.sectionMobile : styles.section}>
        <KeyStatistics symbol={symbol} />
      </div>
      {!isSymbolETF(symbol) && (
        <div className={isMobile ? styles.sectionMobile : styles.section}>
          <CompanyDetails symbol={symbol} />
        </div>
      )}
      {isUserVerified && (
        <div className={isMobile ? styles.sectionMobile : styles.section}>
          <PeopleAlsoTrade symbol={symbol} />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  individualData: state.crm.individualExtendedInfo,
  isSymbolETF: (symbol: string) => isETF(symbol, state),
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(Overview);
